import { useCallback, useEffect, useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useManualQuery, useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Paper from '@zenbusiness/zui/Paper';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import RecipientDetail from '#client/components/MoveMoney/components/SummaryFormComponents/RecipientDetail';
import SenderDetail from '#client/components/MoveMoney/components/SummaryFormComponents/SenderDetail';
import TransactionDetail from '#client/components/MoveMoney/components/SummaryFormComponents/TransactionDetail';
import WirePaymentWaring from '#client/components/MoveMoney/components/WirePaymentWarning';
import { RE_AUTHENTICATE_REQUIRED } from '#client/components/MoveMoney/SendMoneyModal/constants/authen';
import {
  convertCentToUSDText,
  isChaseBank,
  forceLoginWireTransfer
} from '#client/components/MoveMoney/utils/common';
import { CURRENCY } from '#client/constants/currencies';
import { DATE_FORMAT } from '#client/constants/date';
import { COMMON_ERROR_MESSAGE_DESC } from '#client/constants/messages';
import { MFA_VERIFIED, MODAL_NAMES } from '#client/constants/modals';
import { BANKING_PAYMENT_TYPE } from '#client/constants/payment';
import { TOKEN_TYPES } from '#client/constants/tokens';
import {
  CONFIRMATION_FORM_STEP,
  HOW_MUCH_STEP,
  MAP_WIRE_STATUS_TO_ZENBUSINESS_RESPONSE,
  MOVE_MONEY_AGREEMENT,
  TIME_LINE,
  TRANSFER_TYPE,
  WHICH_ACCOUNT_STEP
} from '#client/constants/transfer';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useSessionStorage from '#client/hooks/useSessionStorage';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccount from '#client/hooks/useUnitAccount';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { WIRE_MUTATION } from '#client/mutations';
import { GET_COUNTERPARTY } from '#client/queries';

import { DialogContentStyled, Wrapper } from './IsThisCorrectForm.styles';

export default function IsThisCorrectFormWire({ handleClose, idempotencyKeyFromToken }) {
  const [counterPartyStorage, , , removeCounterPartyStorage] = useSessionStorage(
    'counterParty',
    {}
  );
  const { createErrorToast, createCommonErrorToast } = useToastContext();
  const captureException = useCaptureException();

  const { unitAccount, loading: loadingUnitAccount } = useUnitAccount();
  const { unitAccountId, loading: loadingUnitAccountId } = useUnitAccountId();
  const { clearAllModal } = useModalContext();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { data, setCurrentFormStep, setData } = useFormStep();
  const [getCounterParty, { loading: counterPartyLoading }] = useManualQuery(GET_COUNTERPARTY, {
    noThrow: true
  });

  const { selectedToAccount, fromAccount, entryPage } = data;

  const { amount, counterPartyId, description, idempotencyKey, wireOutgoingFeeText } =
    counterPartyStorage;

  const { getCustomerToken, loading: loadingToken } = useCustomerToken({
    type: TOKEN_TYPES.COMMON
  });

  useModalDisplayTracker({
    elementName: 'authorize_transaction_sm_modal',
    elementText: 'authorize_transaction_sm_modal',
    appPageName: 'active_banking_dashboard'
  });

  // This will save unitAccount to From Account
  useEffect(() => {
    if (unitAccount) {
      setData((prevData) => ({
        ...prevData,
        fromAccount: unitAccount
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitAccount]);

  useEffect(
    () => {
      const shouldLoadData = Boolean(
        counterPartyStorage &&
        Object.keys(counterPartyStorage).length &&
        idempotencyKeyFromToken === counterPartyStorage?.idempotencyKey
      );

      if (shouldLoadData) {
        // Sync data from local storage to data form context
        setData((prevData) => ({
          ...prevData,
          ...counterPartyStorage
        }));
      } else {
        captureException(
          new Error(
            `Idempotency key not match - idempotencyKeyFromToken: ${idempotencyKeyFromToken}, counterPartyStorage: ${JSON.stringify(counterPartyStorage)}`
          ),
          {
            level: 'warning'
          }
        );
        createCommonErrorToast();
        handleClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [counterPartyStorage, idempotencyKeyFromToken]
  );

  // This will call counter party api and save it to To Account
  useEffect(() => {
    const getCounterPartyResponse = async () => {
      const customerToken = await getCustomerToken();

      if (!customerToken) {
        return;
      }

      const { data: counterPartyData, error } = await getCounterParty({
        variables: {
          filter: {
            counterpartyId: counterPartyId,
            customerToken
          }
        },
        skipCache: true
      });

      if (error || !data) {
        createCommonErrorToast({ error });
      } else {
        setData((prevData) => ({
          ...prevData,
          selectedToAccount: counterPartyData?.bankingCounterparty
        }));
      }
    };

    if (counterPartyId) {
      getCounterPartyResponse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessEntityUuid, counterPartyId, unitAccountId]);

  const toName = selectedToAccount?.name ?? '';
  const toBank = selectedToAccount?.bank ?? '';
  const isHideEyeBtn = isChaseBank({
    bank: toBank,
    connectedType: selectedToAccount?.connectedType
  });
  const date = useMemo(() => dayjs(new Date()).format(DATE_FORMAT.MOVE_MONEY), []);
  const amountInUSD = convertCentToUSDText(amount);
  const fromName = fromAccount?.name ?? '';
  const timeline = TIME_LINE.WIRE;

  const finishAllFetching = !counterPartyLoading && !loadingUnitAccount;

  const handleEditTransactionDetails = () => {
    if (finishAllFetching) {
      setCurrentFormStep(HOW_MUCH_STEP);
      removeCounterPartyStorage();
      setData({
        ...data,
        isDisableBack: false
      });
    }
  };

  const handleEditRecipient = () => {
    if (finishAllFetching) {
      setCurrentFormStep(WHICH_ACCOUNT_STEP);
      removeCounterPartyStorage();
      setData({
        ...data,
        isDisableBack: false,
        transferType: TRANSFER_TYPE.STANDARD_ACH.value
      });
    }
  };

  const [wire, { loading: loadingWire }] = useMutation(WIRE_MUTATION, {
    noThrow: true
  });

  const authorizeButtonId = useEnterSubmit();
  const loadingSubmit = loadingUnitAccountId || loadingToken || loadingWire || counterPartyLoading;

  const handleResponse = useCallback(
    (response) => {
      const reason = response?.data?.createBankingPayment?.reason;
      const error = response?.error;
      const errorMessage = MAP_WIRE_STATUS_TO_ZENBUSINESS_RESPONSE[reason];

      if (errorMessage || error) {
        if (error?.errorCode === RE_AUTHENTICATE_REQUIRED) {
          const redirectTo = `${entryPage}?modal=${MODAL_NAMES.SEND_MONEY}&${MFA_VERIFIED}=true`;
          forceLoginWireTransfer(idempotencyKey, redirectTo);
          return;
        }
        createErrorToast({
          title: errorMessage ? 'Wire transfer rejected' : 'Payment failed',
          description: errorMessage || error?.message || COMMON_ERROR_MESSAGE_DESC,
          error
        });
        handleClose();
        clearAllModal();
      } else {
        const dateInit = response?.data?.createBankingPayment?.createdAt;
        const transactionId = response?.data?.createBankingPayment?.transactionId;

        removeCounterPartyStorage();

        // Prepare data for Confirmation Step
        setData({
          ...data,
          confirmationTitle: 'Your wire is scheduled.',
          dateInit,
          transactionId
        });
        setCurrentFormStep(CONFIRMATION_FORM_STEP);
      }
    },
    [
      createErrorToast,
      handleClose,
      clearAllModal,
      entryPage,
      idempotencyKey,
      setData,
      data,
      setCurrentFormStep,
      removeCounterPartyStorage
    ]
  );

  const handleSubmitWire = async () => {
    const customerToken = await getCustomerToken({ forceRefresh: true });

    if (!customerToken) {
      handleClose();
      return;
    }

    const response = await wire({
      variables: {
        input: {
          businessEntityUuid,
          amount: {
            currency: CURRENCY.USD,
            amount
          },
          counterpartyId: data?.selectedToAccount?.id,
          description,
          idempotencyKey,
          customerToken,
          type: BANKING_PAYMENT_TYPE.WIRE
        }
      }
    });
    handleResponse(response);
  };

  const trackAuthorizeClick = useButtonPressedCallback({
    elementName: 'authorize_transfer',
    elementText: 'Authorize',
    appPageName: 'authorize_transaction_sm_modal'
  });

  const handleSubmit = () => {
    try {
      trackAuthorizeClick();

      handleSubmitWire();
    } catch (error) {
      captureException(error);
    }
  };

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper role="form">
              <Stack
                alignItems="center"
                paddingBottom={{
                  xs: '32px',
                  lg: '40px'
                }}
              >
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  Is this correct?
                </Heading>
              </Stack>
              <Paper variant="outlined">
                <TransactionDetail
                  amount={amountInUSD}
                  dateInitiated={date}
                  fee={wireOutgoingFeeText}
                  handleEdit={handleEditTransactionDetails}
                  hasEdit
                  subTitle="Your account activity will show separate transactions for wire amount and wire transfer fee."
                  timeline={timeline}
                  transactionDescription={description}
                  transferType={TRANSFER_TYPE.WIRE.value}
                />
                <Divider />
                <RecipientDetail
                  accountHolder={toName}
                  accountNumber={selectedToAccount?.accountNumber}
                  address={selectedToAccount?.wire?.address ?? {}}
                  bank={toBank}
                  fullAccountNumber={isHideEyeBtn ? '' : selectedToAccount?.fullAccountNumber}
                  handleEdit={handleEditRecipient}
                  hasEdit
                  loading={counterPartyLoading}
                />
                <Divider />
                <SenderDetail
                  accountHolder={fromName}
                  accountNumber={fromAccount?.accountNumber}
                  bank="ZenBusiness"
                  fullAccountNumber={fromAccount?.accountNumber}
                  loading={loadingUnitAccount}
                />
              </Paper>
              <WirePaymentWaring />
              <Box
                marginTop={{
                  xs: '32px',
                  lg: '40px'
                }}
              >
                <Text
                  align="left"
                  color="tertiary"
                  size="sm"
                >
                  {MOVE_MONEY_AGREEMENT.LINE_1}
                </Text>
                <br />
                <Text
                  align="left"
                  color="tertiary"
                  size="sm"
                >
                  {MOVE_MONEY_AGREEMENT.LINE_2}
                </Text>
                <br />
                <Text
                  align="left"
                  color="tertiary"
                  size="sm"
                >
                  {MOVE_MONEY_AGREEMENT.LINE_3}
                </Text>
              </Box>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid type={ACTION_BUTTON_TYPE.TERTIARY}>
        <LoadingButton
          color="primary"
          disabled={loadingSubmit}
          fullWidth
          id={authorizeButtonId}
          loading={loadingSubmit}
          onClick={handleSubmit}
          size="large"
          variant="contained"
        >
          Authorize
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

IsThisCorrectFormWire.propTypes = {
  handleClose: PropTypes.func.isRequired,
  idempotencyKeyFromToken: PropTypes.string.isRequired
};
